<template>
  <!-- No select-all listener: with `selectAll` unset, PrimeVue's toggle-all goes through
       updateModel and emits `change` like any other pick. -->
  <MultiSelect
    v-model="model"
    :options
    optionLabel="label"
    optionValue="value"
    :placeholder="placeholder ?? $t('common.filter')"
    class="p-column-filter w-100"
    :filter="searchable"
    appendTo="body"
    :focusOnHover="false"
    @change="$emit('filter')"
  >
    <template v-if="searchable" #filtericon>
      <font-awesome-icon
        icon="search"
        class="text-muted pv-multiselect-filter-icon"
      />
    </template>
    <template #value="{ value, placeholder: shownPlaceholder }">
      <span
        v-if="value && value.length"
        class="fw-bold"
        :class="`text-${tone}`"
      >
        {{ activeLabel ?? $t("common.active") }}
      </span>
      <span v-else :class="{ 'text-muted': mutedPlaceholder }">
        {{ shownPlaceholder }}
      </span>
    </template>
    <template v-if="$slots.option" #option="{ option }">
      <slot name="option" :option></slot>
    </template>
  </MultiSelect>
</template>

<script setup lang="ts">
import MultiSelect from "primevue/multiselect";
import type { VNode } from "vue";

export type ColumnFilterValue = string | number | boolean | null;

export interface ColumnFilterOption {
  label: string;
  value: ColumnFilterValue;
}

/**
 * A DataTable column's multi-select filter: bind the slot's `filterModel.value` with `v-model` and
 * call its `filterCallback()` on `filter`. The field shows "Active" rather than the chosen values,
 * which would not fit a column header; `tone` colours that label (danger flags a filter the page
 * could not apply). Pass an `#option` slot only to render options as more than their label.
 */
withDefaults(
  defineProps<{
    options: ColumnFilterOption[];
    /** Adds a search box above the options, for long lists. */
    searchable?: boolean;
    /** Defaults to "Filter". */
    placeholder?: string;
    /** Defaults to the localized `common.active`. */
    activeLabel?: string;
    tone?: "primary" | "danger";
    mutedPlaceholder?: boolean;
  }>(),
  { placeholder: undefined, activeLabel: undefined, tone: "primary" }
);

defineEmits<{
  filter: [];
}>();

defineSlots<{
  option?: (props: { option: ColumnFilterOption }) => VNode[];
}>();

const model = defineModel<ColumnFilterValue[] | null>();
</script>
